import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { deleteMedida, getMedidaById, getPacienteInformeById, updateMedida } from '../api/antropometriaApi';
import {
  calculateImc,
  calculatePerimetroMedio,
  calculatePesoBrazosPiernas,
  calculateSmi,
} from '../utils/antropometriaCalculations';
import { formatDateDDMMYYYY } from '../utils/antropometriaFormatters';

const EMPTY_FIELDS = {
  talla: '',
  peso: '',
  perimetroAbdominal1: '',
  perimetroAbdominal2: '',
  perimetroAbdominal3: '',
  mediaPerimetroAbdominal: '',
  grasa: '',
  nivelGrasa: '',
  musculo: '',
  imc: '',
  bd: '',
  bi: '',
  pd: '',
  pi: '',
  pesoBrazosPiernas: '',
  smi: '',
};

export const useAntropometriaViewer = ({ medidaId, pacienteId }) => {
  const navigate = useNavigate();
  const [patientInfo, setPatientInfo] = useState({
    codigo: '',
    createdAt: '',
    nombre: '',
    apellidos: '',
    codigoInforme: '',
  });
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [isEditing, setIsEditing] = useState(false);
  const [openGood, setOpenGood] = useState(false);
  const [originalValues, setOriginalValues] = useState(EMPTY_FIELDS);

  useEffect(() => {
    if (!pacienteId || !medidaId) return;

    Promise.all([getPacienteInformeById(pacienteId), getMedidaById(medidaId)])
      .then(([pacienteData, medidasData]) => {
        setPatientInfo({
          codigo: pacienteData.id || '',
          createdAt: formatDateDDMMYYYY(medidasData.createdAt),
          nombre: pacienteData.firstname || '',
          apellidos: pacienteData.lastname || '',
          codigoInforme: medidasData.codigo || '',
        });
        setFields({
          talla: medidasData.talla !== null ? String(medidasData.talla) : '',
          peso: medidasData.peso !== null ? String(medidasData.peso) : '',
          perimetroAbdominal1: medidasData.perimetroAbdominal1 !== null ? String(medidasData.perimetroAbdominal1) : '',
          perimetroAbdominal2: medidasData.perimetroAbdominal2 !== null ? String(medidasData.perimetroAbdominal2) : '',
          perimetroAbdominal3: medidasData.perimetroAbdominal3 !== null ? String(medidasData.perimetroAbdominal3) : '',
          mediaPerimetroAbdominal: medidasData.mediaPerimetroAbdominal !== null ? String(medidasData.mediaPerimetroAbdominal) : '',
          grasa: medidasData.grasa !== null ? String(medidasData.grasa) : '',
          nivelGrasa: medidasData.nivelGrasa !== null ? String(medidasData.nivelGrasa) : '',
          musculo: medidasData.musculo !== null ? String(medidasData.musculo) : '',
          imc: medidasData.imc !== null ? String(medidasData.imc) : '',
          bd: medidasData.bd !== null ? String(medidasData.bd) : '',
          bi: medidasData.bi !== null ? String(medidasData.bi) : '',
          pd: medidasData.pd !== null ? String(medidasData.pd) : '',
          pi: medidasData.pi !== null ? String(medidasData.pi) : '',
          pesoBrazosPiernas: medidasData.pesoBrazosPiernas !== null ? String(medidasData.pesoBrazosPiernas) : '',
          smi: medidasData.smi !== null ? String(medidasData.smi) : '',
        });
      })
      .catch((error) => {
        console.error('Error fetching antropometria data:', error);
      });
  }, [medidaId, pacienteId]);

  useEffect(() => {
    const imc = calculateImc({ peso: fields.peso, tallaCm: fields.talla });
    const media = calculatePerimetroMedio({
      p1: fields.perimetroAbdominal1,
      p2: fields.perimetroAbdominal2,
      p3: fields.perimetroAbdominal3,
    });
    const total = calculatePesoBrazosPiernas({
      bd: fields.bd,
      bi: fields.bi,
      pd: fields.pd,
      pi: fields.pi,
    });
    const smi = calculateSmi({
      bd: fields.bd,
      bi: fields.bi,
      pd: fields.pd,
      pi: fields.pi,
      tallaCm: fields.talla,
    });

    setFields((prev) => ({
      ...prev,
      imc,
      mediaPerimetroAbdominal: media,
      pesoBrazosPiernas: total,
      smi,
    }));
  }, [
    fields.talla,
    fields.peso,
    fields.perimetroAbdominal1,
    fields.perimetroAbdominal2,
    fields.perimetroAbdominal3,
    fields.bd,
    fields.bi,
    fields.pd,
    fields.pi,
  ]);

  const setField = (name, value) => {
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    setOriginalValues(fields);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFields(originalValues);
    setIsEditing(false);
  };

  const handleSave = () => {
    updateMedida({
      codigoInforme: patientInfo.codigoInforme,
      payload: {
        talla: fields.talla !== '' ? parseFloat(fields.talla) : null,
        peso: fields.peso !== '' ? parseFloat(fields.peso) : null,
        perimetroAbdominal1: fields.perimetroAbdominal1 !== '' ? parseFloat(fields.perimetroAbdominal1) : null,
        perimetroAbdominal2: fields.perimetroAbdominal2 !== '' ? parseFloat(fields.perimetroAbdominal2) : null,
        perimetroAbdominal3: fields.perimetroAbdominal3 !== '' ? parseFloat(fields.perimetroAbdominal3) : null,
        mediaPerimetroAbdominal: fields.mediaPerimetroAbdominal !== '' ? parseFloat(fields.mediaPerimetroAbdominal) : null,
        grasa: fields.grasa !== '' ? parseFloat(fields.grasa) : null,
        nivelGrasa: fields.nivelGrasa !== '' ? parseInt(fields.nivelGrasa, 10) : null,
        musculo: fields.musculo !== '' ? parseFloat(fields.musculo) : null,
        imc: fields.imc !== '' ? parseFloat(fields.imc) : null,
        bd: fields.bd !== '' ? parseFloat(fields.bd) : null,
        bi: fields.bi !== '' ? parseFloat(fields.bi) : null,
        pd: fields.pd !== '' ? parseFloat(fields.pd) : null,
        pi: fields.pi !== '' ? parseFloat(fields.pi) : null,
        pesoBrazosPiernas: fields.pesoBrazosPiernas !== '' ? parseFloat(fields.pesoBrazosPiernas) : null,
        smi: fields.smi !== '' ? parseFloat(fields.smi) : null,
      },
    })
      .then(() => {
        setOpenGood(true);
        setIsEditing(false);
      })
      .catch((error) => {
        console.error('Error saving changes:', error);
      });
  };

  const handleDelete = () => {
    if (!window.confirm('¿Está seguro de que desea eliminar este informe?')) return;

    deleteMedida(patientInfo.codigoInforme)
      .then(() => {
        navigate('/home');
      })
      .catch((error) => {
        console.error('Error deleting report:', error);
      });
  };

  const handleExportPDF = () => {
    setIsEditing(false);
    const input = document.getElementById('pdf-content');
    html2canvas(input, {
      scale: 2,
      backgroundColor: null,
      useCORS: true,
      ignoreElements: (element) => element.tagName === 'BUTTON',
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const imgProperties = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`medidas_antropometricas_${patientInfo.nombre}${patientInfo.apellidos}_${patientInfo.createdAt}.pdf`);
      })
      .catch((error) => {
        console.error('Error exporting PDF:', error);
      });
  };

  const parseMetric = (value) => {
    const number = parseFloat(value);
    return Number.isNaN(number) ? 'N/D' : number.toFixed(2);
  };

  const imcRisk = useMemo(() => {
    const value = parseFloat(fields.imc);
    if (Number.isNaN(value)) return { label: 'Sin clasificación', color: 'default' };
    if (value < 18.5) return { label: 'Bajo peso', color: 'warning' };
    if (value < 25) return { label: 'Normopeso', color: 'success' };
    if (value < 30) return { label: 'Sobrepeso', color: 'warning' };
    return { label: 'Obesidad', color: 'error' };
  }, [fields.imc]);

  const metrics = useMemo(
    () => [
      { label: 'IMC', value: parseMetric(fields.imc), unit: 'kg/m²' },
      { label: 'Perímetro medio', value: parseMetric(fields.mediaPerimetroAbdominal), unit: 'cm' },
      { label: 'SMI', value: parseMetric(fields.smi), unit: 'kg/m²' },
    ],
    [fields.imc, fields.mediaPerimetroAbdominal, fields.smi]
  );

  return {
    patientInfo,
    fields,
    isEditing,
    openGood,
    metrics,
    imcRisk,
    setOpenGood,
    setField,
    handleEdit,
    handleCancel,
    handleSave,
    handleDelete,
    handleExportPDF,
  };
};
